import { z } from "zod";
import { defineEndpoint } from "./endpoint.js";
/**
 * Plans and entitlements (docs/specs/platform.md Part 2, SA-05 … SA-07). The keys are fixed in code; their values
 * are data (plans, add-ons, overrides) so a new plan needs no deploy.
 */
export const LIMIT_KEYS = [
    "locations.max",
    "users.max",
    "devices.max",
    "products.max",
    "orders.monthly.max",
    "storefront.landing_pages.max",
];
export const CREDIT_KEYS = ["sms.credits"];
export const FEATURE_KEYS = [
    "feature.pos",
    "feature.multi_location",
    "feature.custom_roles",
    "feature.api",
    "feature.storefront",
    "feature.custom_domain",
    // PL-02 (docs/specs/plan-catalogue.md): registered and seeded; each is checked from its PL-07 … task on.
    "feature.premium_themes",
    "feature.remove_branding",
    "feature.shopper_accounts",
    "feature.digital_products",
    "feature.online_payments",
    "feature.courier_integration",
    "feature.purchasing",
    "feature.accounting",
    "feature.advanced_reports",
];
export const ENTITLEMENT_KEYS = [...LIMIT_KEYS, ...CREDIT_KEYS, ...FEATURE_KEYS];
/** Keys an add-on can raise (SA-07). */
export const ADDON_KEYS = ["locations.max", "devices.max", "sms.credits"];
/** Sections of the feature list (PL-01); the Super Admin places each line in one, the words are in i18n. */
export const ENTITLEMENT_GROUPS = [
    "usage",
    "channels",
    "stock",
    "delivery",
    "payments",
    "customers",
    "accounts_reports",
    "team",
    "integrations",
    "support",
];
/** Code keys (limit / credit / feature) and the Super Admin's own `display.*` lines, which nothing checks. */
export const ENTITLEMENT_KINDS = ["limit", "credit", "feature", "display"];
export const DISPLAY_KEY_PATTERN = /^display\.[a-z][a-z0-9_]{1,50}$/;
export const entitlementKeySchema = z.enum(ENTITLEMENT_KEYS);
/** Sent with 422 `PLAN_LIMIT_REACHED` / `FEATURE_NOT_IN_PLAN`; `limit` null = a feature that is off. */
export const entitlementProblemSchema = z.object({
    key: entitlementKeySchema,
    limit: z.number().int().nullable(),
    usage: z.number().int(),
});
const limitSchema = z.object({
    key: z.enum(LIMIT_KEYS),
    /** null = unlimited. */
    limit: z.number().int().nullable(),
    usage: z.number().int(),
});
export const entitlementsSchema = z.object({
    plan: z.object({ key: z.string(), nameEn: z.string(), nameBn: z.string() }),
    features: z.record(z.enum(FEATURE_KEYS), z.boolean()),
    limits: z.array(limitSchema),
    sms: z.object({
        /** This month's allowance (`sms.credits`) and what is left of it; packs never expire. */
        monthlyAllowance: z.number().int(),
        monthlyLeft: z.number().int(),
        packLeft: z.number().int(),
    }),
});
export const getEntitlements = defineEndpoint({
    name: "getEntitlements",
    method: "GET",
    path: "/v1/entitlements",
    access: { signedIn: true },
    summary: "The business's plan: features, limits with usage, SMS credits",
    tag: "platform",
    status: 200,
    response: entitlementsSchema,
});
// Events -------------------------------------------------------------------------------------------------------
export const entitlementThresholdEventSchema = z.object({
    key: z.enum([...LIMIT_KEYS, ...CREDIT_KEYS]),
    level: z.union([z.literal(80), z.literal(100)]),
    limit: z.number().int(),
    usage: z.number().int(),
    /** `'-'` for standing limits, `YYYY-MM` for monthly ones. */
    period: z.string(),
});
//# sourceMappingURL=entitlements.js.map